import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "../App";
import Navigation from "../components/Navigation";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Camera,
  Upload,
  Sparkles,
  Download,
  RefreshCw,
  Image,
  Wand2,
  Quote,
  Loader2,
  Check,
  X,
  Share2,
  Heart,
} from "lucide-react";
import { toast } from "sonner";

export default function AICamera() {
  const [filters, setFilters] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState("warm_glow");
  const [selectedQuote, setSelectedQuote] = useState("");
  const [customQuote, setCustomQuote] = useState("");
  const [uploadedImage, setUploadedImage] = useState(null);
  const [enhancedImage, setEnhancedImage] = useState(null);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generateLoading, setGenerateLoading] = useState(false);
  const [customPrompt, setCustomPrompt] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchFiltersAndQuotes();
  }, []);

  const fetchFiltersAndQuotes = async () => {
    try {
      const [filtersRes, quotesRes] = await Promise.all([
        axios.get(`${API}/camera/filters`),
        axios.get(`${API}/camera/quotes`),
      ]);
      setFilters(filtersRes.data.filters || []);
      setQuotes(quotesRes.data.quotes || []);
      setSelectedQuote(quotesRes.data.random_quote || "");
    } catch (error) {
      console.error("Failed to load camera data");
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadedImage(reader.result);
        setEnhancedImage(null);
      };
      reader.readAsDataURL(file);
    }
  };

  const enhanceImage = async () => {
    if (!uploadedImage) {
      toast.error("Please upload an image first");
      return;
    }

    setLoading(true);
    try {
      // Extract base64 data from data URL
      const base64Data = uploadedImage.split(",")[1];
      
      const response = await axios.post(`${API}/camera/enhance`, {
        image_base64: base64Data,
        filter_id: selectedFilter,
        quote: customQuote || selectedQuote,
        custom_prompt: customPrompt || null,
      });

      if (response.data.success) {
        setEnhancedImage(`data:image/png;base64,${response.data.enhanced_image}`);
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to enhance image");
    } finally {
      setLoading(false);
    }
  };

  const generateInspirationalImage = async () => {
    setGenerateLoading(true);
    try {
      const response = await axios.post(`${API}/camera/generate-inspirational`, null, {
        params: {
          prompt: customPrompt || null,
          quote: customQuote || selectedQuote,
        },
      });

      if (response.data.success) {
        setGeneratedImage(`data:image/png;base64,${response.data.image}`);
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate image");
    } finally {
      setGenerateLoading(false);
    }
  };

  const downloadImage = (imageData, filename) => {
    const link = document.createElement("a");
    link.href = imageData;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getRandomQuote = () => {
    const randomQuote = quotes[Math.floor(Math.random() * quotes.length)];
    setSelectedQuote(randomQuote);
    setCustomQuote("");
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-pink-500/10 via-primary/10 to-purple-500/10"></div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center mx-auto mb-4">
              <Camera className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              AI Camera
            </h1>
            <p className="text-muted-foreground text-lg max-w-xl mx-auto">
              Transform your photos with AI filters and inspiring quotes
            </p>
          </div>
        </div>
      </div>

      {/* Main content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="enhance" className="space-y-8">
          <TabsList className="grid w-full grid-cols-2 max-w-md mx-auto">
            <TabsTrigger value="enhance" className="flex items-center gap-2">
              <Wand2 className="w-4 h-4" />
              Enhance Photo
            </TabsTrigger>
            <TabsTrigger value="generate" className="flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              Create New
            </TabsTrigger>
          </TabsList>

          {/* Enhance Photo Tab */}
          <TabsContent value="enhance" className="space-y-8">
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Left Column - Upload & Settings */}
              <div className="space-y-6">
                {/* Upload Area */}
                <div className="card-happy">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Upload className="w-5 h-5 text-primary" />
                    Upload Your Photo
                  </h3>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    accept="image/*"
                    className="hidden"
                    data-testid="camera-file-input"
                  />
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-border rounded-2xl p-8 text-center cursor-pointer hover:border-primary transition-colors"
                    data-testid="camera-upload-area"
                  >
                    {uploadedImage ? (
                      <div className="space-y-4">
                        <img
                          src={uploadedImage}
                          alt="Uploaded"
                          className="max-h-48 mx-auto rounded-xl"
                        />
                        <p className="text-sm text-muted-foreground">Click to change photo</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <Image className="w-12 h-12 text-muted-foreground mx-auto" />
                        <p className="text-muted-foreground">
                          Click or drag to upload a photo
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Supports JPG, PNG, WEBP
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Filter Selection */}
                <div className="card-happy">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-amber-500" />
                    Choose Filter
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    {filters.map((filter) => (
                      <button
                        key={filter.id}
                        onClick={() => setSelectedFilter(filter.id)}
                        data-testid={`filter-${filter.id}`}
                        className={`p-3 rounded-xl text-left transition-all ${
                          selectedFilter === filter.id
                            ? "bg-primary text-white ring-2 ring-primary"
                            : "bg-muted hover:bg-muted/80"
                        }`}
                      >
                        <p className="font-medium text-sm">{filter.name}</p>
                        <p className={`text-xs ${selectedFilter === filter.id ? "text-white/80" : "text-muted-foreground"}`}>
                          {filter.description}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Quote Selection */}
                <div className="card-happy">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold flex items-center gap-2">
                      <Quote className="w-5 h-5 text-purple-500" />
                      Inspiring Quote
                    </h3>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={getRandomQuote}
                      className="text-primary"
                      data-testid="random-quote-btn"
                    >
                      <RefreshCw className="w-4 h-4 mr-1" />
                      Random
                    </Button>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="p-4 bg-purple-50 rounded-xl">
                      <p className="text-purple-700 font-handwriting text-lg">
                        &ldquo;{selectedQuote}&rdquo;
                      </p>
                    </div>
                    
                    <div>
                      <Label htmlFor="custom-quote">Or write your own:</Label>
                      <Textarea
                        id="custom-quote"
                        placeholder="Enter your custom quote..."
                        value={customQuote}
                        onChange={(e) => setCustomQuote(e.target.value)}
                        className="input-happy mt-2"
                        data-testid="custom-quote-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Enhance Button */}
                <Button
                  onClick={enhanceImage}
                  disabled={!uploadedImage || loading}
                  className="btn-primary w-full"
                  data-testid="enhance-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Enhancing with AI...
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-5 h-5 mr-2" />
                      Enhance Photo
                    </>
                  )}
                </Button>
              </div>

              {/* Right Column - Result */}
              <div className="card-happy">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Heart className="w-5 h-5 text-pink-500" />
                  Your Enhanced Photo
                </h3>
                
                {enhancedImage ? (
                  <div className="space-y-4">
                    <div className="rounded-2xl overflow-hidden bg-muted">
                      <img
                        src={enhancedImage}
                        alt="Enhanced"
                        className="w-full"
                        data-testid="enhanced-image"
                      />
                    </div>
                    <div className="flex gap-3">
                      <Button
                        onClick={() => downloadImage(enhancedImage, "happybook-enhanced.png")}
                        className="flex-1"
                        variant="outline"
                        data-testid="download-enhanced-btn"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download
                      </Button>
                      <Button
                        onClick={() => {
                          setEnhancedImage(null);
                          setUploadedImage(null);
                        }}
                        variant="ghost"
                      >
                        <X className="w-4 h-4 mr-2" />
                        Clear
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="aspect-square rounded-2xl bg-muted flex flex-col items-center justify-center">
                    <Camera className="w-16 h-16 text-muted-foreground mb-4" />
                    <p className="text-muted-foreground text-center">
                      Your enhanced photo will appear here
                    </p>
                    <p className="text-sm text-muted-foreground mt-2">
                      Upload a photo and click Enhance
                    </p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Generate New Tab */}
          <TabsContent value="generate" className="space-y-8">
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Left Column - Settings */}
              <div className="space-y-6">
                <div className="card-happy">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-amber-500" />
                    Create Inspirational Image
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    Generate a beautiful, AI-created inspirational image with your chosen quote.
                  </p>

                  <div className="space-y-4">
                    <div>
                      <Label>Custom Description (Optional)</Label>
                      <Textarea
                        placeholder="Describe the image you want... e.g., 'A peaceful sunrise over mountains with warm colors'"
                        value={customPrompt}
                        onChange={(e) => setCustomPrompt(e.target.value)}
                        className="input-happy mt-2"
                        data-testid="generate-prompt-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Quote Selection for Generation */}
                <div className="card-happy">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold flex items-center gap-2">
                      <Quote className="w-5 h-5 text-purple-500" />
                      Choose Your Quote
                    </h3>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={getRandomQuote}
                      className="text-primary"
                    >
                      <RefreshCw className="w-4 h-4 mr-1" />
                      Random
                    </Button>
                  </div>
                  
                  <ScrollArea className="h-48">
                    <div className="space-y-2">
                      {quotes.map((quote, i) => (
                        <button
                          key={i}
                          onClick={() => { setSelectedQuote(quote); setCustomQuote(""); }}
                          className={`w-full p-3 rounded-xl text-left text-sm transition-all ${
                            selectedQuote === quote && !customQuote
                              ? "bg-purple-100 text-purple-700 ring-1 ring-purple-300"
                              : "bg-muted hover:bg-muted/80"
                          }`}
                        >
                          &ldquo;{quote}&rdquo;
                        </button>
                      ))}
                    </div>
                  </ScrollArea>

                  <div className="mt-4">
                    <Label>Or write your own:</Label>
                    <Textarea
                      placeholder="Your custom inspirational quote..."
                      value={customQuote}
                      onChange={(e) => setCustomQuote(e.target.value)}
                      className="input-happy mt-2"
                    />
                  </div>
                </div>

                <Button
                  onClick={generateInspirationalImage}
                  disabled={generateLoading}
                  className="btn-primary w-full"
                  data-testid="generate-btn"
                >
                  {generateLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Creating Magic...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5 mr-2" />
                      Generate Inspirational Image
                    </>
                  )}
                </Button>
              </div>

              {/* Right Column - Generated Result */}
              <div className="card-happy">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Heart className="w-5 h-5 text-pink-500" />
                  Your Creation
                </h3>
                
                {generatedImage ? (
                  <div className="space-y-4">
                    <div className="rounded-2xl overflow-hidden bg-muted">
                      <img
                        src={generatedImage}
                        alt="Generated"
                        className="w-full"
                        data-testid="generated-image"
                      />
                    </div>
                    <div className="flex gap-3">
                      <Button
                        onClick={() => downloadImage(generatedImage, "happybook-inspirational.png")}
                        className="flex-1"
                        variant="outline"
                        data-testid="download-generated-btn"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download
                      </Button>
                      <Button
                        onClick={() => setGeneratedImage(null)}
                        variant="ghost"
                      >
                        <X className="w-4 h-4 mr-2" />
                        Clear
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="aspect-square rounded-2xl bg-muted flex flex-col items-center justify-center">
                    <Sparkles className="w-16 h-16 text-muted-foreground mb-4" />
                    <p className="text-muted-foreground text-center">
                      Your AI-generated image will appear here
                    </p>
                    <p className="text-sm text-muted-foreground mt-2">
                      Click &ldquo;Generate&rdquo; to create magic
                    </p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      <Navigation />
    </div>
  );
}
